/**
 * DetailsFormSection Component
 * 
 * A generic form section component that renders multiple form fields based on configuration.
 * Provides consistent form layouts across Events and Bulletins detail sections.
 * 
 * Features:
 * - Field-driven rendering via configuration array
 * - Automatic form validation display
 * - Support for text inputs, multiline text areas
 * - Configurable field properties (required, placeholder, etc.)
 * - Consistent FormSection and FormInput usage
 * - Type-flexible for different form data structures
 * 
 * Field Configuration:
 * - key: Field name in form data
 * - label: Display label for the field
 * - placeholder: Input placeholder text
 * - required: Shows asterisk and enables validation
 * - multiline: Enables textarea mode
 * - numberOfLines: Height for multiline inputs
 * - autoCapitalize: Text capitalization behavior
 * - autoCorrect: Enable/disable autocorrection
 * 
 * Usage:
 * ```tsx
 * const fields = [
 *   { key: 'title', label: 'Title', placeholder: 'Enter title', required: true },
 *   { key: 'description', label: 'Description', placeholder: 'Enter description', multiline: true, numberOfLines: 4 }
 * ];
 * 
 * <DetailsFormSection
 *   title="Event Details"
 *   formData={formData}
 *   errors={errors}
 *   onFieldChange={handleFieldChange}
 *   fields={fields}
 * />
 * ```
 */
import React from 'react';
import { View, StyleSheet } from 'react-native';
import { FormSection, FormInput } from '@/components';

interface FormField {
  key: string;
  label: string;
  placeholder: string;
  required?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  autoCorrect?: boolean;
}

interface DetailsFormSectionProps {
  title: string;
  subtitle?: string;
  formData: any;
  errors: any;
  onFieldChange: (field: any, value: any) => void;
  fields: FormField[];
}

export const DetailsFormSection: React.FC<DetailsFormSectionProps> = ({
  title,
  subtitle = '',
  formData,
  errors,
  onFieldChange,
  fields,
}) => {
  return (
    <FormSection title={title} subtitle={subtitle}>
      <View style={styles.container}>
        {fields.map((field) => (
          <FormInput
            key={field.key}
            label={field.label}
            value={formData[field.key] || ''}
            onChangeText={(value: string) => onFieldChange(field.key, value)}
            placeholder={field.placeholder}
            error={errors[field.key]}
            required={field.required}
            multiline={field.multiline}
            numberOfLines={field.numberOfLines}
            autoCapitalize={field.autoCapitalize || 'sentences'}
            autoCorrect={field.autoCorrect !== false}
          />
        ))}
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  container: {
    // No additional styles needed
  },
});
